import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Very tolerant column-name matcher (accents & separators stripped).
const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
const HEADER_MAP = {
  first_name: ["firstname", "prenom", "prenoms", "given", "givenname"],
  last_name: ["lastname", "nom", "familyname", "surname", "name"],
  date_of_birth: ["dateofbirth", "datenaissance", "ddn", "dob", "birthdate", "birthday", "naissance"],
  email: ["email", "mail", "courriel", "adresseemail"],
};

// Parses a French-friendly date (DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, or ISO YYYY-MM-DD)
// into strict ISO YYYY-MM-DD, returning null for anything unparseable.
function toIsoDate(v) {
  if (!v) return null;
  const s = v.toString().trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
  if (iso.test(s)) return s;
  const fr = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/;
  const m = s.match(fr);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return null;
}

// Splits a CSV line handling quoted values (",;\t as separator).
function splitLine(line, sep) {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; }
      else quoted = !quoted;
    } else if (c === sep && !quoted) {
      out.push(cur); cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out.map((x) => x.trim());
}

function detectSep(header) {
  const c = header.match(/[,;\t]/);
  return c ? c[0] : ",";
}

function parseCsv(text) {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim().length);
  if (lines.length < 2) return { header: [], rows: [] };
  const sep = detectSep(lines[0]);
  const header = splitLine(lines[0], sep);
  const rows = lines.slice(1).map((l) => splitLine(l, sep));
  return { header, rows, sep };
}

function mapHeaderToField(h) {
  const n = norm(h);
  for (const [field, aliases] of Object.entries(HEADER_MAP)) {
    if (aliases.includes(n)) return field;
  }
  return null;
}

export default function CsvImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setError(null); setResult(null); };
  const closeAll = () => { reset(); onClose(); };

  const handleFile = (f) => {
    setError(null); setResult(null);
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setError("Fichier trop volumineux (max 2 Mo)"); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      const { header, rows } = parseCsv(reader.result);
      if (!header.length || !rows.length) { setError("Le fichier semble vide."); setPreview(null); return; }
      const mapping = header.map(mapHeaderToField);
      const required = ["first_name", "last_name", "date_of_birth"];
      const missing = required.filter((r) => !mapping.includes(r));
      if (missing.length) {
        const labels = { first_name: "Prénom", last_name: "Nom", date_of_birth: "Date de naissance" };
        setError(`Colonnes manquantes : ${missing.map((m) => labels[m]).join(", ")}. Renommez vos en-têtes en Prénom, Nom, Date de naissance (optionnels : Email, Sexe).`);
        return;
      }
      // Build normalized rows
      const patients = rows.map((r) => {
        const obj = {};
        mapping.forEach((f, i) => { if (f) obj[f] = (r[i] ?? "").trim(); });
        if (obj.date_of_birth) obj.date_of_birth = toIsoDate(obj.date_of_birth) || obj.date_of_birth;
        return obj;
      });
      setPreview({ header, rows, mapping, patients });
    };
    reader.readAsText(f, "utf-8");
  };

  const submit = async () => {
    if (!preview?.patients?.length) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/patient-records/bulk-import", { patients: preview.patients });
      setResult(data);
      toast.success(`${data.created} patient${data.created > 1 ? "s" : ""} importé${data.created > 1 ? "s" : ""}`);
      onImported?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur d'import");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); }}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-[#E1E9F0]">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold tracking-tight text-[#1E3A5F]">
            <Upload size={20} weight="duotone" className="text-[#3A8FA5]" /> Importer des patients (CSV)
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {!result && (
            <>
              <div className="rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 text-[13px] text-[#5F758F]">
                <p className="font-semibold text-[#1E3A5F] mb-1">Format attendu</p>
                Un fichier CSV (séparateur virgule ou point-virgule) avec les colonnes : <strong>Prénom</strong>, <strong>Nom</strong>, <strong>Date de naissance</strong> (obligatoires) et <em>Email</em> (facultatif). La date accepte JJ/MM/AAAA ou AAAA-MM-JJ. Limite : 1 000 patients par import.
              </div>

              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#C7DEE6] bg-[#F4F7FB] rounded-[14px] px-6 py-8 cursor-pointer hover:border-[#3A8FA5] transition-colors" data-testid="csv-drop">
                <Upload size={24} weight="duotone" className="text-[#3A8FA5]" />
                <span className="text-[14px] font-semibold text-[#1E3A5F]">{file?.name || "Choisir un fichier CSV"}</span>
                <span className="text-[12px] text-[#5F758F]">Max 2 Mo</span>
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="csv-input" />
              </label>

              {error && (
                <div className="rounded-[10px] bg-[#FFE0DC] border border-[#EEC1BB] px-4 py-3 text-[13px] text-[#8B2A1E] flex gap-2">
                  <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {preview && !error && (
                <div>
                  <p className="text-[13px] text-[#5F758F] mb-2">
                    Aperçu : <strong className="text-[#1E3A5F]">{preview.patients.length}</strong> patient{preview.patients.length > 1 ? "s" : ""} détecté{preview.patients.length > 1 ? "s" : ""}. Colonnes reconnues :{" "}
                    {preview.mapping.filter(Boolean).map((f) => <span key={f} className="inline-block bg-[#E6F1F5] border border-[#C7DEE6] text-[#3A8FA5] rounded-full px-2 py-0.5 mr-1 text-[11px] font-semibold">{f}</span>)}
                  </p>
                  <div className="border border-[#E1E9F0] rounded-[10px] overflow-hidden">
                    <table className="w-full text-[12.5px]">
                      <thead className="bg-[#F4F7FB] text-[#5F758F]">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Prénom</th>
                          <th className="text-left px-3 py-2 font-semibold">Nom</th>
                          <th className="text-left px-3 py-2 font-semibold">Naissance</th>
                          <th className="text-left px-3 py-2 font-semibold">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.patients.slice(0, 5).map((p, i) => (
                          <tr key={`csv-p-${i}-${p.email || p.last_name || p.first_name || ""}`} className="border-t border-[#E1E9F0]">
                            <td className="px-3 py-1.5">{p.first_name || <span className="text-[#D45A3F]">—</span>}</td>
                            <td className="px-3 py-1.5">{p.last_name || <span className="text-[#D45A3F]">—</span>}</td>
                            <td className="px-3 py-1.5">{p.date_of_birth || <span className="text-[#D45A3F]">—</span>}</td>
                            <td className="px-3 py-1.5">{p.email || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.patients.length > 5 && (
                      <div className="text-center text-[12px] text-[#5F758F] py-2 border-t border-[#E1E9F0] bg-[#F4F7FB]">… et {preview.patients.length - 5} autre{preview.patients.length - 5 > 1 ? "s" : ""}</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={20} weight="fill" className="text-[#3A8FA5]" />
                <p className="font-bold text-[#1E3A5F]">Import terminé</p>
              </div>
              <ul className="text-[13px] text-[#1E3A5F] space-y-1">
                <li>✓ <strong>{result.created}</strong> patient{result.created > 1 ? "s" : ""} créé{result.created > 1 ? "s" : ""}</li>
                {result.skipped_dupes > 0 && <li>· <strong>{result.skipped_dupes}</strong> doublon{result.skipped_dupes > 1 ? "s" : ""} email ignoré{result.skipped_dupes > 1 ? "s" : ""}</li>}
                {result.errors?.length > 0 && <li>⚠ <strong>{result.errors.length}</strong> ligne{result.errors.length > 1 ? "s" : ""} en erreur (voir détail ci-dessous)</li>}
              </ul>
              {result.errors?.length > 0 && (
                <div className="mt-3 text-[12px] text-[#8B2A1E] max-h-40 overflow-y-auto space-y-0.5">
                  {result.errors.slice(0, 20).map((e) => <div key={`csv-err-${e.row}`}>Ligne {e.row} : {e.error}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[#E1E9F0] px-6 py-3 flex justify-end gap-2 bg-[#F4F7FB]">
          {result ? (
            <Button onClick={closeAll} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="csv-done">Terminer</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeAll}>Annuler</Button>
              <Button
                onClick={submit}
                disabled={!preview || !!error || busy}
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40"
                data-testid="csv-submit"
              >
                {busy ? "Import en cours…" : `Importer ${preview?.patients?.length || ""} patient${(preview?.patients?.length || 0) > 1 ? "s" : ""}`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
